import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useClient } from "../../../context/ClientContext";
import { useGamification } from "../../../context/GamificationContext";
import { useLanguage } from "../../../context/LanguageContext";
import ResponsiveScratchCard from "./ResponsiveScratchCard";
import scratchCover from "../../../assets/images/games/scratch/scratch.png";

const getSocialMediaImageUrl = (rewardConfig, rewardAmount, languageId, languageCode) => {
  const key = `social-media-${Number(rewardAmount)}`;
  const bucket = rewardConfig?.[key];
  if (!bucket) return "";

  if (languageId != null && bucket?.[languageId]) return bucket[languageId];
  if (languageCode && bucket?.[languageCode]) return bucket[languageCode];

  const first = Object.values(bucket).find(Boolean);
  return typeof first === "string" ? first : "";
};

export default function StampRallySeries({
  series,
  campaignRewards,
  campaignId,
  languageId,
  languageCode,
}) {
  const navigate = useNavigate();
  const { tenanturlname } = useParams();
  const { client } = useClient();
  const { t } = useLanguage();
  const { issueReward } = useGamification();

  const baseReward = useMemo(() => {
    return campaignRewards.find(
      (r) => (r.challengeId === 0 || r.challengeId == null) && r.seriesId === series.seriesId
    );
  }, [campaignRewards, series.seriesId]);

  const isSeriesCompleted = !!baseReward;
  const isSeriesRewardScratched = !!campaignRewards.find(
    (r) =>
      (r.challengeId === 0 || r.challengeId == null) &&
      r.seriesId === series.seriesId &&
      Number(r.statusId) >= 6
  );

  const rewardAmount = baseReward?.rewardAmount ?? 0;
  const tnxId = baseReward?.transactionId ?? "";
  const rewardConfig = baseReward?.campaignConfig;
  const rewardOption = baseReward?.campaignRewardOptions;

  const [issuing, setIssuing] = useState(false);

  const handleSeriesComplete = async () => {
    if (issuing) return;
    if (!client?.clientId || !campaignId || !tnxId) return;

    try {
      setIssuing(true);

      const ok = await issueReward(client.clientId, {
        campaignId,
        transactionId: tnxId,
        rewardAmount: Number(rewardAmount),
        action: "Stamp Rally",
      });

      if (!ok) return;

      const imageUrl = getSocialMediaImageUrl(
        rewardConfig,
        rewardAmount,
        languageId,
        languageCode
      );

      navigate(`/${tenanturlname}/gamification/congratulation`, {
        state: {
          reward: Number(rewardAmount),
          chances: 0,
          campaignId,
          congratsImage: rewardConfig?.congratulation,
          imageUrl,
          tnxId,
          campaignType: "CHALLENGE",
        },
      });
    } finally {
      setIssuing(false);
    }
  };

  return (
    <div className="rounded-2xl border bg-[#C7E9FD] p-3 sm:p-4">
      <div className="mb-3 text-lg sm:text-[20px] font-bold text-[#1434CB]">
        {series?.seriesName || "Stamp Card"}
      </div>

      {/* On mobile: wrap stamps (responsive). On desktop: keep a single row with horizontal scroll if needed. */}
      <div className="flex items-center justify-center md:justify-start gap-2 flex-wrap md:flex-nowrap overflow-x-visible md:overflow-x-auto pb-2">
        {(series?.challenges || []).map((step) => {
          const isFree = String(step?.qualifyingAction || "").includes("FREE");
          const isDone =
            isFree || campaignRewards.some((r) => r.challengeId === step.challengeId);

          const stampImage =
            (isFree
              ? step?.qualifyingDetails?.freeDetails?.rewardOptions?.stampImage
              : step?.qualifyingDetails?.transactionDetails?.rewardOptions?.stampImage) || "";

          return (
            <div
              key={step.challengeId}
              className="flex items-center justify-center rounded-full bg-white"
              style={{
                width: "clamp(48px, 14vw, 132px)",
                height: "clamp(48px, 14vw, 132px)",
                border: isDone
                  ? "clamp(2px, 0.8vw, 10px) solid #1434CB"
                  : "clamp(2px, 0.8vw, 10px) solid #C7C7C7",
                flex: "0 0 auto",
              }}
              title={step?.challengeName || ""}
            >
              {stampImage ? (
                <img
                  src={stampImage}
                  alt={step?.challengeName || "stamp"}
                  style={{
                    width: "clamp(28px, 10vw, 120px)",
                    height: "clamp(28px, 10vw, 120px)",
                    objectFit: "cover",
                    filter: isDone ? "none" : "grayscale(100%)",
                    borderRadius: "9999px",
                  }}
                />
              ) : (
                <div className="text-xs text-gray-500">{isDone ? "✓" : "•"}</div>
              )}
            </div>
          );
        })}

        <div
          className="flex items-center justify-center rounded-full bg-white"
          style={{
            width: "clamp(64px, 18vw, 140px)",
            height: "clamp(64px, 18vw, 140px)",
            border: isSeriesCompleted
              ? "clamp(2px, 0.8vw, 12px) solid #FEC200"
              : "clamp(2px, 0.8vw, 12px) solid #C7C7C7",
            flex: "0 0 auto",
            overflow: "hidden",
          }}
        >
          {isSeriesCompleted ? (
            isSeriesRewardScratched ? (
              rewardOption?.image ? (
                <img
                  src={rewardOption.image}
                  alt="reward"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <div className="text-center text-sm font-bold text-theme-secondary">
                  {Number(rewardAmount)} {t("gamification.yen") || "円"}
                </div>
              )
            ) : (
              <ResponsiveScratchCard
                settings={{
                  image: rewardOption?.image,
                  coverImage: scratchCover,
                  finishPercent: 70,
                  onComplete: handleSeriesComplete,
                  coverColor: "#D9D9D9",
                }}
              >
                <div className="flex h-full w-full items-center justify-center bg-white">
                  {rewardOption?.image ? (
                    <img
                      src={rewardOption.image}
                      alt="reward"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <div className="text-center text-sm font-bold text-theme-secondary px-2">
                      Scratch to reveal
                      <div className="mt-1">
                        {Number(rewardAmount)} {t("gamification.yen") || "円"}
                      </div>
                    </div>
                  )}
                </div>
              </ResponsiveScratchCard>
            )
          ) : (
            <img
              src={scratchCover}
              alt="Locked reward"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                filter: "grayscale(100%)",
                opacity: 0.9,
              }}
            />
          )}
        </div>
      </div>

      {issuing && (
        <div className="mt-3 text-sm text-gray-700">
          {t("homePage.loading") || "Loading..."}
        </div>
      )}
    </div>
  );
}


